import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Play, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PortfolioPage({ 
  pageType, 
  title, 
  subtitle,
  showCategories = false,
  categories = []
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalItem, setModalItem] = useState(null);
  
  // Items per page: mobile 6, desktop 20
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setItemsPerPage(window.innerWidth < 768 ? 6 : 20);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [pageType, selectedCategory]);

  useEffect(() => {
    // Update displayed items based on current page
    const endIndex = currentPage * itemsPerPage;
    setDisplayedItems(items.slice(0, endIndex));
  }, [items, currentPage, itemsPerPage]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let url = `${API}/portfolio/${pageType}`;
      if (selectedCategory) {
        url += `?category=${selectedCategory}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setItems(data.items || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching portfolio items:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const hasMore = displayedItems.length < items.length;

  const getVideoUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            MUAS Academy
          </h1>
        </div>
      </header>

      {/* Title Section */}
      <section className="py-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
          {title}
        </h1>
        {subtitle && (
          <p className="text-gray-600 text-lg max-w-2xl mx-auto px-4">{subtitle}</p>
        )}
      </section>

      {/* Categories Filter (if applicable) */}
      {showCategories && categories.length > 0 && (
        <section className="container mx-auto px-4 mb-8">
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              onClick={() => setSelectedCategory(null)}
              className="rounded-full"
            >
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat.value)}
                className="rounded-full"
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* Gallery Grid */}
      <section className="container mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        ) : displayedItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No items yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Grid: 2 columns mobile, 4 columns desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {displayedItems.map((item) => (
                <div
                  key={item.id}
                  className="relative aspect-square rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                  onClick={() => setModalItem(item)}
                >
                  {item.media_type === "video" ? (
                    <>
                      <video
                        src={getVideoUrl(item.thumbnail || item.url)}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-all">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-pink-600 ml-1" fill="currentColor" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={getVideoUrl(item.url)}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-10">
                <Button
                  onClick={loadMore}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-3 rounded-full"
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Modal for viewing full item */}
      {modalItem && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setModalItem(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setModalItem(null)}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {modalItem.media_type === "video" ? (
              <video
                src={getVideoUrl(modalItem.url)}
                className="w-full h-full max-h-[90vh] object-contain"
                controls
                autoPlay
              />
            ) : (
              <img
                src={getVideoUrl(modalItem.url)}
                alt=""
                className="w-full h-full max-h-[90vh] object-contain"
              />
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}
